import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  CreditNote,
  CreditNoteAddForm,
  CreditNoteUpdateForm,
  CreditType,
  PARTNER_TYPE_FK_FIELD,
  PartnerType,
  PARTNER_TYPE_LABEL,
  addCreditNote,
  refreshCreditNotesList,
  updateCreditNote,
} from "src/store/reducers/creditNotes";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomReactSelectWithLabel from "src/layouts/form/CustomReactSelectWithLabel";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import { fetchListHotels } from "src/store/reducers/hotels";
import { fetchListCabVendors } from "src/store/reducers/cabVendors";
import { fetchListTaxis } from "src/store/reducers/taxis";
import { fetchListAirlines } from "src/store/reducers/airlines";
import { fetchListCruiseLines } from "src/store/reducers/cruiseLines";
import { fetchListInsuranceProviders } from "src/store/reducers/insuranceProviders";
import { fetchListAgents } from "src/store/reducers/agents";

type ICreditNoteFormCanvas = {
  Title: string;
  show: boolean;
  hide: () => void;
  editData?: CreditNote;
};

const PARTNER_TYPES: { value: PartnerType; label: string }[] = [
  { value: "hotel", label: PARTNER_TYPE_LABEL.hotel },
  { value: "cab", label: PARTNER_TYPE_LABEL.cab },
  { value: "taxi", label: PARTNER_TYPE_LABEL.taxi },
  { value: "flight_agency", label: PARTNER_TYPE_LABEL.flight_agency },
  { value: "cruise", label: PARTNER_TYPE_LABEL.cruise },
  { value: "insurance", label: PARTNER_TYPE_LABEL.insurance },
  { value: "agent", label: PARTNER_TYPE_LABEL.agent },
];

const CreditNoteFormCanvas = ({
  Title,
  show,
  hide,
  editData,
}: ICreditNoteFormCanvas) => {
  const [pending, setPending] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const dispatch = useAppDispatch();

  const isEdit = !!editData;

  const resolveInitialPartnerValue = (data?: CreditNote): string => {
    if (!data || data.credit_type !== CreditType.Received) return "";
    const fkField = data.partner_type
      ? PARTNER_TYPE_FK_FIELD[data.partner_type]
      : null;
    if (!fkField) return "";
    return (data as any)[fkField] || "";
  };

  const {
    handleChange,
    setErrors,
    values,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setFieldValue,
  } = useFormik<{
    credit_type: string;
    amount: string;
    currency: string;
    reason: string;
    expiry_date: string;
    notes: string;
    // credit_type = 1
    customer_name: string;
    customer_number: string;
    customer_email: string;
    // credit_type = 2
    partner_type: string;
    partner_id: string;
  }>({
    initialValues: {
      credit_type: editData ? String(editData.credit_type) : "",
      amount: editData?.amount || "",
      currency: editData?.currency || "INR",
      reason: editData?.reason || "",
      expiry_date: editData?.expiry_date || "",
      notes: editData?.notes || "",
      customer_name: editData?.customer_name || "",
      customer_number: editData?.customer_number
        ? String(editData.customer_number)
        : "",
      customer_email: editData?.customer_email || "",
      partner_type: editData?.partner_type || "",
      partner_id: resolveInitialPartnerValue(editData),
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      credit_type: isEdit
        ? Yup.string()
        : Yup.string().required("Credit type is required"),
      amount: isEdit
        ? Yup.string()
        : Yup.number()
            .required("Amount is required")
            .typeError("Amount must be a number")
            .positive("Amount must be positive"),
      customer_name: Yup.string().when("credit_type", {
        is: (val: string) => val === String(CreditType.Given) && !isEdit,
        then: (s) => s.required("Customer name is required"),
        otherwise: (s) => s,
      }),
      customer_number: Yup.string().when("credit_type", {
        is: (val: string) => val === String(CreditType.Given) && !isEdit,
        then: (s) => s.required("Customer phone is required"),
        otherwise: (s) => s,
      }),
      partner_type: Yup.string().when("credit_type", {
        is: (val: string) => val === String(CreditType.Received) && !isEdit,
        then: (s) => s.required("Partner type is required"),
        otherwise: (s) => s,
      }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      setPending(true);
      if (isEdit) {
        submitEdit(vals);
      } else {
        submitAdd(vals);
      }
    },
  });

  const abortRef = useRef<AbortController>();

  const fetchPartnerOptions = useCallback(
    async (partnerType: string) => {
      if (abortRef.current) abortRef.current.abort();
      setPartnerOptions([]);
      try {
        let data: any[] = [];
        switch (partnerType as PartnerType) {
          case "hotel": {
            const res = await dispatch(fetchListHotels()).unwrap();
            data = res.map((h: any) => ({ label: h.name, value: h.id }));
            break;
          }
          case "cab": {
            const res = await dispatch(fetchListCabVendors()).unwrap();
            data = res.map((c: any) => ({
              label: c.contact_person_name,
              value: c.id,
            }));
            break;
          }
          case "taxi": {
            const res = await dispatch(fetchListTaxis()).unwrap();
            data = res.map((t: any) => ({
              label: t.vehicle_name,
              value: t.id,
            }));
            break;
          }
          case "flight_agency": {
            const res = await dispatch(fetchListAirlines()).unwrap();
            data = res.map((a: any) => ({
              label: a.airline_name,
              value: a.id,
            }));
            break;
          }
          case "cruise": {
            const res = await dispatch(fetchListCruiseLines()).unwrap();
            data = res.map((c: any) => ({
              label: c.cruise_line_name,
              value: c.id,
            }));
            break;
          }
          case "insurance": {
            const res = await dispatch(fetchListInsuranceProviders()).unwrap();
            data = res.map((i: any) => ({
              label: i.provider_name,
              value: i.id,
            }));
            break;
          }
          case "agent": {
            const res = await dispatch(fetchListAgents()).unwrap();
            data = res.map((a: any) => ({ label: a.name, value: a.id }));
            break;
          }
          default:
            break;
        }
        setPartnerOptions(data);
      } catch (_) {
        // silently fail — partner list is optional
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (values.partner_type) {
      setFieldValue("partner_id", "");
      fetchPartnerOptions(values.partner_type);
    } else {
      setPartnerOptions([]);
    }
  }, [values.partner_type, fetchPartnerOptions, setFieldValue]);

  // Pre-load partner options when editing with an existing partner_type
  useEffect(() => {
    if (show && editData?.partner_type) {
      fetchPartnerOptions(editData.partner_type);
    }
  }, [show, editData?.partner_type, fetchPartnerOptions]);

  const submitAdd = useCallback(
    async (vals: typeof values) => {
      try {
        const payload: CreditNoteAddForm = {
          credit_type: Number(vals.credit_type) as CreditType,
          amount: vals.amount,
          currency: vals.currency || undefined,
          reason: vals.reason || undefined,
          expiry_date: vals.expiry_date || null,
          notes: vals.notes || undefined,
        };

        if (Number(vals.credit_type) === CreditType.Given) {
          payload.customer_name = vals.customer_name;
          payload.customer_number = vals.customer_number;
          if (vals.customer_email) payload.customer_email = vals.customer_email;
        }

        if (Number(vals.credit_type) === CreditType.Received) {
          payload.partner_type = vals.partner_type as PartnerType;
          if (vals.partner_id && vals.partner_type) {
            const fkField = PARTNER_TYPE_FK_FIELD[vals.partner_type as PartnerType];
            (payload as any)[fkField] = vals.partner_id;
          }
        }

        await dispatch(addCreditNote(payload)).unwrap();
        toast.success("Credit note added successfully");
        dispatch(refreshCreditNotesList());
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, hide, setErrors]
  );

  const submitEdit = useCallback(
    async (vals: typeof values) => {
      if (!editData) return;
      try {
        const payload: CreditNoteUpdateForm = { id: editData.id };
        if (vals.reason !== editData.reason) payload.reason = vals.reason;
        if (vals.notes !== editData.notes) payload.notes = vals.notes;
        if (vals.expiry_date !== (editData.expiry_date || ""))
          payload.expiry_date = vals.expiry_date || null;

        if (editData.credit_type === CreditType.Given) {
          if (vals.customer_name) payload.customer_name = vals.customer_name;
          if (vals.customer_number)
            payload.customer_number = vals.customer_number;
          if (vals.customer_email)
            payload.customer_email = vals.customer_email;
        }

        if (editData.credit_type === CreditType.Received) {
          if (vals.partner_type)
            payload.partner_type = vals.partner_type as PartnerType;
          if (vals.partner_id && vals.partner_type) {
            const fkField =
              PARTNER_TYPE_FK_FIELD[vals.partner_type as PartnerType];
            (payload as any)[fkField] = vals.partner_id;
          }
        }

        await dispatch(updateCreditNote(payload)).unwrap();
        toast.success("Credit note updated successfully");
        dispatch(refreshCreditNotesList());
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, editData, hide, setErrors]
  );

  useEffect(() => {
    resetForm();
    setPartnerOptions([]);
  }, [resetForm, show]);

  const isGiven = values.credit_type === String(CreditType.Given);
  const isReceived = values.credit_type === String(CreditType.Received);

  return (
    <Offcanvas
      show={show}
      onHide={hide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h5 className="modal-title" id="#gridSystemModal">
          {Title}
        </h5>
        <button type="button" className="btn-close" onClick={hide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Credit Type — immutable after creation */}
              {!isEdit && (
                <div className="col-xl-6 mb-3">
                  <CustomSelectWithLabel
                    id="credit_type"
                    labelText="Credit Type"
                    required={true}
                    handleChange={handleChange}
                    value={values.credit_type}
                    touched={touched.credit_type}
                    errors={errors.credit_type}
                  >
                    <option value="">Select Credit Type</option>
                    <option value={String(CreditType.Given)}>
                      Credit Given (to Customer)
                    </option>
                    <option value={String(CreditType.Received)}>
                      Credit Received (from Partner)
                    </option>
                  </CustomSelectWithLabel>
                </div>
              )}

              {/* Amount — immutable after creation */}
              {!isEdit && (
                <div className="col-xl-6 mb-3">
                  <CustomInputWithLabel
                    id="amount"
                    inputType="number"
                    labelText="Amount"
                    required={true}
                    handleChange={handleChange}
                    value={values.amount}
                    touched={touched.amount}
                    errors={errors.amount}
                  />
                </div>
              )}

              {!isEdit && (
                <div className="col-xl-6 mb-3">
                  <CustomInputWithLabel
                    id="currency"
                    labelText="Currency"
                    handleChange={handleChange}
                    value={values.currency}
                    touched={touched.currency}
                    errors={errors.currency}
                  />
                </div>
              )}

              {/* Credit Given fields */}
              {isGiven && !isEdit && (
                <>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_name"
                      labelText="Customer Name"
                      required={true}
                      handleChange={handleChange}
                      value={values.customer_name}
                      touched={touched.customer_name}
                      errors={errors.customer_name}
                    />
                  </div>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_number"
                      inputType="tel"
                      labelText="Customer Phone"
                      required={true}
                      handleChange={handleChange}
                      value={values.customer_number}
                      touched={touched.customer_number}
                      errors={errors.customer_number}
                    />
                  </div>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_email"
                      inputType="email"
                      labelText="Customer Email"
                      handleChange={handleChange}
                      value={values.customer_email}
                      touched={touched.customer_email}
                      errors={errors.customer_email}
                    />
                  </div>
                </>
              )}

              {/* Edit: editable Given fields */}
              {isEdit && editData?.credit_type === CreditType.Given && (
                <>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_name"
                      labelText="Customer Name"
                      handleChange={handleChange}
                      value={values.customer_name}
                      touched={touched.customer_name}
                      errors={errors.customer_name}
                    />
                  </div>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_number"
                      inputType="tel"
                      labelText="Customer Phone"
                      handleChange={handleChange}
                      value={values.customer_number}
                      touched={touched.customer_number}
                      errors={errors.customer_number}
                    />
                  </div>
                  <div className="col-xl-6 mb-3">
                    <CustomInputWithLabel
                      id="customer_email"
                      inputType="email"
                      labelText="Customer Email"
                      handleChange={handleChange}
                      value={values.customer_email}
                      touched={touched.customer_email}
                      errors={errors.customer_email}
                    />
                  </div>
                </>
              )}

              {/* Credit Received fields */}
              {(isReceived || (isEdit && editData?.credit_type === CreditType.Received)) && (
                <>
                  <div className="col-xl-6 mb-3">
                    <CustomSelectWithLabel
                      id="partner_type"
                      labelText="Partner Type"
                      required={!isEdit}
                      handleChange={handleChange}
                      value={values.partner_type}
                      touched={touched.partner_type}
                      errors={errors.partner_type}
                    >
                      <option value="">Select Partner Type</option>
                      {PARTNER_TYPES.map((pt) => (
                        <option value={pt.value} key={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    </CustomSelectWithLabel>
                  </div>
                  {partnerOptions.length > 0 && (
                    <div className="col-xl-6 mb-3">
                      <CustomReactSelectWithLabel
                        id="partner_id"
                        labelText={
                          PARTNER_TYPE_LABEL[values.partner_type as PartnerType] ||
                          "Partner"
                        }
                        placeholder={
                          PARTNER_TYPE_LABEL[values.partner_type as PartnerType] ||
                          "Partner"
                        }
                        options={partnerOptions}
                        handleChange={handleChange}
                        value={
                          partnerOptions.find(
                            (o) => o.value === values.partner_id
                          ) || ""
                        }
                        touched={touched.partner_id}
                        errors={errors.partner_id}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Common editable fields */}
              <div className="col-xl-12 mb-3">
                <CustomInputWithLabel
                  id="reason"
                  labelText="Reason"
                  handleChange={handleChange}
                  value={values.reason}
                  touched={touched.reason}
                  errors={errors.reason}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="expiry_date"
                  labelText="Expiry Date"
                  placeholder="Expiry Date"
                  pickerType="YYYY-MM-DD"
                  handleChange={(date) => setFieldValue("expiry_date", date)}
                  value={values.expiry_date}
                  touched={touched.expiry_date}
                  errors={errors.expiry_date}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="notes"
                  labelText="Notes"
                  handleChange={handleChange}
                  value={values.notes}
                  touched={touched.notes}
                  errors={errors.notes}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={pending}
                  className="btn btn-primary me-1"
                >
                  {pending ? "Saving..." : "Submit"}
                </button>
                <Link
                  to={"#"}
                  onClick={hide}
                  className="btn btn-danger light ms-1"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default CreditNoteFormCanvas;
